import React from 'react';

// Skeleton base component with shimmer effect
const SkeletonBase = ({ className = '', style = {} }) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      style={{
        background: 'linear-gradient(90deg, #e5e7eb 25%, #d1d5db 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s ease-in-out infinite',
        ...style
      }}
    />
  );
};

// Card Skeleton
export const CardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
          <SkeletonBase className="h-48 w-full" />
          <div className="p-6">
            <SkeletonBase className="h-6 w-3/4 mb-4" />
            <SkeletonBase className="h-4 w-full mb-2" />
            <SkeletonBase className="h-4 w-5/6 mb-4" />
            <div className="flex items-center justify-between">
              <SkeletonBase className="h-4 w-24" />
              <SkeletonBase className="h-4 w-16" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// News Card Skeleton
export const NewsCardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
          <div className="md:flex">
            <SkeletonBase className="h-48 md:h-auto md:w-64 flex-shrink-0" />
            <div className="p-6 flex-1">
              <SkeletonBase className="h-6 w-3/4 mb-3" />
              <SkeletonBase className="h-4 w-full mb-2" />
              <SkeletonBase className="h-4 w-5/6 mb-4" />
              <div className="flex items-center gap-4">
                <SkeletonBase className="h-4 w-24" />
                <SkeletonBase className="h-4 w-20" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// Table Row Skeleton
export const TableRowSkeleton = ({ count = 5, columns = 6 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, rowIndex) => (
        <tr key={rowIndex} className="bg-white">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
              <SkeletonBase className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

// List Item Skeleton
export const ListItemSkeleton = ({ count = 5 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 p-4 border-b border-gray-200">
          <SkeletonBase className="h-12 w-12 rounded-full flex-shrink-0" />
          <div className="flex-1">
            <SkeletonBase className="h-5 w-3/4 mb-2" />
            <SkeletonBase className="h-4 w-1/2" />
          </div>
          <SkeletonBase className="h-8 w-20" />
        </div>
      ))}
    </>
  );
};

// Event Card Skeleton
export const EventCardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
          <SkeletonBase className="h-48 w-full" />
          <div className="p-6">
            <SkeletonBase className="h-6 w-3/4 mb-3" />
            <SkeletonBase className="h-4 w-full mb-2" />
            <SkeletonBase className="h-4 w-5/6 mb-4" />
            <div className="flex items-center gap-4 mb-4">
              <SkeletonBase className="h-4 w-32" />
              <SkeletonBase className="h-4 w-24" />
            </div>
            <SkeletonBase className="h-10 w-32" />
          </div>
        </div>
      ))}
    </>
  );
};

// Featured Event Skeleton
export const FeaturedEventSkeleton = () => {
  return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg overflow-hidden shadow-lg h-full">
      <div className="p-6 text-white h-full flex flex-col">
        <SkeletonBase className="h-5 w-24 mb-4 bg-blue-400" />
        <SkeletonBase className="h-48 w-full mb-4 rounded-lg bg-blue-400" />
        <SkeletonBase className="h-8 w-3/4 mb-3 bg-blue-400" />
        <SkeletonBase className="h-4 w-full mb-2 bg-blue-400" />
        <SkeletonBase className="h-4 w-5/6 mb-4 bg-blue-400" />
        <div className="flex gap-4 mb-4">
          <SkeletonBase className="h-4 w-32 bg-blue-400" />
          <SkeletonBase className="h-4 w-24 bg-blue-400" />
        </div>
        <SkeletonBase className="h-10 w-32 mt-auto bg-blue-400" />
      </div>
    </div>
  );
};

// Calendar Skeleton
export const CalendarSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <SkeletonBase className="h-6 w-32" />
        <div className="flex gap-2">
          <SkeletonBase className="h-8 w-8 rounded" />
          <SkeletonBase className="h-8 w-8 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <SkeletonBase key={i} className="h-6 w-full" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 35 }).map((_, i) => (
          <SkeletonBase key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    </div>
  );
};

// Sponsor Card Skeleton
export const SponsorCardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-md p-6 flex flex-col items-center ${className}`}>
          <SkeletonBase className="h-24 w-24 rounded-full mb-4" />
          <SkeletonBase className="h-5 w-3/4 mb-2" />
          <SkeletonBase className="h-4 w-1/2" />
        </div>
      ))}
    </>
  );
};

// Advertisement Card Skeleton
export const AdvertisementCardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
          <SkeletonBase className="h-48 w-full" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <SkeletonBase className="h-4 w-24" />
              <SkeletonBase className="h-6 w-20 rounded-full" />
            </div>
            <SkeletonBase className="h-4 w-full mb-2" />
            <SkeletonBase className="h-4 w-full mb-2" />
            <SkeletonBase className="h-4 w-5/6 mb-4" />
            <div className="flex items-center justify-between">
              <SkeletonBase className="h-4 w-20" />
              <SkeletonBase className="h-4 w-16" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// Page Content Skeleton
export const PageContentSkeleton = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <SkeletonBase className="h-10 w-64 mb-8 mx-auto" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CardSkeleton count={6} />
      </div>
    </div>
  );
};

// Add shimmer animation to global styles (only once)
if (typeof document !== 'undefined' && !document.head.querySelector('style[data-skeleton]')) {
  const style = document.createElement('style');
  style.setAttribute('data-skeleton', 'true');
  style.textContent = `
    @keyframes shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
    .animate-\\[shimmer_1\\.5s_ease-in-out_infinite\\] {
      animation: shimmer 1.5s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

export default CardSkeleton;

